import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  TrendingUp, 
  Eye, 
  Heart, 
  MessageCircle, 
  Share2, 
  Users, 
  MousePointer,
  AlertTriangle,
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  RefreshCw,
  LogIn
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { slideUp, fadeIn } from '@/lib/animations';
import useAuth from '../hooks/useAuth';
import apiService from '../services/api';
import { Project } from '../types';

// Mock analytics data generator
const generateMockAnalytics = (projectId: string, projectName: string) => {
  const baseViews = Math.floor(Math.random() * 50000) + 5000;
  const impressions = baseViews * (Math.random() * 3 + 2); // 2-5x views
  const engagementRate = Math.random() * 8 + 1; // 1-9%
  const clickThroughRate = Math.random() * 3 + 0.5; // 0.5-3.5%
  
  return {
    projectId,
    projectName,
    totalViews: baseViews,
    totalImpressions: Math.floor(impressions),
    engagementRate: Number(engagementRate.toFixed(2)),
    clickThroughRate: Number(clickThroughRate.toFixed(2)),
    likes: Math.floor(baseViews * 0.05 * (engagementRate / 5)),
    comments: Math.floor(baseViews * 0.01 * (engagementRate / 5)),
    shares: Math.floor(baseViews * 0.02 * (engagementRate / 5)),
    subscribers: Math.floor(baseViews * 0.003),
    weeklyData: Array.from({ length: 7 }, (_, i) => ({
      day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
      views: Math.floor(baseViews / 7 * (Math.random() * 0.6 + 0.7)),
      engagement: Math.floor(baseViews * engagementRate / 100 / 7 * (Math.random() * 0.6 + 0.7)),
    })),
    platformBreakdown: {
      YouTube: Math.floor(baseViews * 0.4),
      TikTok: Math.floor(baseViews * 0.35),
      Instagram: Math.floor(baseViews * 0.25),
    },
    demographics: {
      age: {
        '18-24': 25,
        '25-34': 35,
        '35-44': 20,
        '45-54': 15,
        '55+': 5
      },
      gender: {
        'Male': 52,
        'Female': 45,
        'Other': 3
      }
    }
  };
};

const AnalyticsDashboard: React.FC = () => {
  console.log('[ANALYTICS] AnalyticsDashboard component mounted/rendered');
  
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshAuth } = useAuth();
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState(0);

  // Debug authentication state
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    console.log('[ANALYTICS] Authentication state:', {
      isAuthenticated,
      hasToken: !!token,
      user: user?.id,
      tokenLength: token?.length
    });
  }, [isAuthenticated, user]);

  // Fetch projects with enhanced error handling
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      setAuthError(false);
      
      // Check authentication first
      if (!isAuthenticated) {
        console.log('[ANALYTICS] Not authenticated, attempting to refresh auth');
        const authSuccess = await refreshAuth();
        if (!authSuccess) {
          setAuthError(true);
          setError('Authentication required. Please log in again.');
          return;
        }
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        setAuthError(true);
        setError('No authentication token found. Please log in.');
        return;
      }

      console.log('[ANALYTICS] Fetching projects with token:', token.substring(0, 20) + '...');
      
      const response = await apiService.getProjects();
      
      if (response.data?.status === 'success') {
        // Filter to only show completed projects
        const completedProjects = response.data.data.filter(
          (project: Project) => project.status === 'Processing Complete' || 
                               project.status === 'Uploaded to Socials'
        );
        setProjects(completedProjects);
        setRetryCount(0); // Reset retry count on success
        console.log('[ANALYTICS] Successfully fetched projects:', completedProjects.length);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch projects');
      }
    } catch (error: any) {
      console.error('[ANALYTICS] Error fetching projects:', error);
      
      // Handle different types of errors
      if (error.response?.status === 401) {
        setAuthError(true);
        setError('Session expired. Please log in again.');
        // Try to refresh auth once
        if (retryCount < 1) {
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            refreshAuth().then(success => {
              if (success) {
                fetchProjects();
              }
            });
          }, 1000);
        }
      } else if (error.response?.status === 403) {
        setAuthError(true);
        setError('Access denied. Please check your permissions.');
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        setError('Request timed out. Please check your connection and try again.');
      } else {
        setError(error.message || 'Failed to load projects');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    // Generate mock analytics for the selected project
    const mockAnalytics = generateMockAnalytics(project.id, project.name);
    setAnalytics(mockAnalytics);
  };

  const handleBack = () => {
    navigate('/projects');
  };

  const handleRetry = () => {
    setRetryCount(0);
    fetchProjects();
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleDebugAuth = async () => {
    try {
      console.log('[ANALYTICS] Running authentication debug test...');
      
      // Test connectivity first
      const connectivityResponse = await apiService.testConnectivity();
      console.log('[ANALYTICS] Connectivity test result:', connectivityResponse.data);
      
      // Test authentication
      const authResponse = await apiService.testAuth();
      console.log('[ANALYTICS] Auth test result:', authResponse.data);
      
      alert('Debug tests passed! Check console for details.');
    } catch (error: any) {
      console.error('[ANALYTICS] Debug test failed:', error);
      alert(`Debug test failed: ${error.message}`);
    }
  };

  const MetricCard = ({ icon: Icon, title, value, subtitle, trend, color = "primary" }: any) => (
    <div className="bg-background rounded-xl border border-border p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className={cn(
          "w-12 h-12 rounded-xl flex items-center justify-center",
          color === "primary" && "bg-primary/10",
          color === "green" && "bg-green-100",
          color === "blue" && "bg-blue-100",
          color === "purple" && "bg-purple-100"
        )}>
          <Icon className={cn(
            "h-6 w-6",
            color === "primary" && "text-primary",
            color === "green" && "text-green-600",
            color === "blue" && "text-blue-600",
            color === "purple" && "text-purple-600"
          )} />
        </div>
        {trend && (
          <div className={cn(
            "flex items-center text-sm font-medium",
            trend > 0 ? "text-green-600" : "text-red-600"
          )}>
            <TrendingUp className="h-3 w-3 mr-1" />
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </div>
      <h3 className="text-sm font-medium text-muted-foreground mb-1">{title}</h3>
      <p className="text-2xl font-bold mb-1">{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );

  const SimpleBarChart = ({ data, title }: any) => (
    <div className="bg-background rounded-xl border border-border p-6">
      <h3 className="text-lg font-semibold mb-6">{title}</h3>
      <div className="space-y-4">
        {data.map((item: any, index: number) => {
          const maxValue = Math.max(...data.map((d: any) => d.views || d.value || 0));
          const width = ((item.views || item.value || 0) / maxValue) * 100;
          
          return (
            <div key={index} className="flex items-center space-x-4">
              <div className="w-12 text-sm font-medium text-muted-foreground">
                {item.day || item.label}
              </div>
              <div className="flex-1">
                <div className="w-full bg-secondary rounded-full h-3">
                  <div 
                    className="bg-primary h-3 rounded-full transition-all duration-700"
                    style={{ width: `${width}%` }}
                  />
                </div>
              </div>
              <div className="w-16 text-sm font-medium text-right">
                {(item.views || item.value || 0).toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading analytics dashboard...</p>
        </div>
      </div>
    );
  }

  // Show authentication error
  if (authError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LogIn className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={handleLogin} className="flex items-center gap-2">
              <LogIn className="h-4 w-4" />
              Log In
            </Button>
            <Button variant="outline" onClick={handleRetry} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className={cn(slideUp(), "mb-8")}>
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-4 -ml-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
                <p className="text-muted-foreground">Track performance across all your published content</p>
              </div>
            </div>
            
            {/* Debug info in development */}
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-muted-foreground">
                Auth: {isAuthenticated ? '✓' : '✗'} | 
                Token: {localStorage.getItem('authToken') ? '✓' : '✗'} | 
                User: {user?.id?.substring(0, 8) || 'None'}
              </div>
            )}
            
            {/* Debug button for development */}
            {process.env.NODE_ENV === 'development' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDebugAuth}
                className="ml-4"
              >
                Debug Auth
              </Button>
            )}
          </div>
        </div>

        {!selectedProject ? (
          /* Project Selection */
          <div className={cn(fadeIn(200), "max-w-4xl mx-auto")}>
            <div className="text-center mb-8">
              <h2 className="text-xl font-semibold mb-2">Select a Project</h2>
              <p className="text-muted-foreground">Choose a project to view its analytics and performance metrics</p>
            </div>

            {error && !authError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-red-600">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetry}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Retry
                  </Button>
                </div>
              </div>
            )}

            {projects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="group cursor-pointer bg-background rounded-xl border border-border p-6 hover:border-primary/30 hover:shadow-md transition-all duration-200"
                    onClick={() => handleProjectSelect(project)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Activity className="h-6 w-6 text-primary" />
                      </div>
                      <span className={cn(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        project.status === 'Uploaded to Socials' ? 
                          'bg-green-100 text-green-800' : 
                          'bg-blue-100 text-blue-800'
                      )}>
                        {project.status === 'Uploaded to Socials' ? 'Published' : 'Complete'}
                      </span>
                    </div>
                    
                    <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                      {project.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {project.description || 'No description'}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{project.content_types?.name}</span>
                      <span>{new Date(project.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="font-medium mb-2">No Analytics Available</h3>
                <p className="text-muted-foreground mb-4">
                  You need completed projects to view analytics
                </p>
                <Button onClick={() => navigate('/projects/create')}>
                  Create Your First Project
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Analytics Dashboard */
          <div className="space-y-8">
            {/* Project Header */}
            <div className={cn(fadeIn(100), "flex items-center justify-between")}>
              <div>
                <h2 className="text-2xl font-bold">{selectedProject.name}</h2>
                <p className="text-muted-foreground">{selectedProject.content_types?.name} • {selectedProject.tone_new || 'Not set'}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setSelectedProject(null)}
              >
                Choose Different Project
              </Button>
            </div>

            {/* Low Engagement Warning */}
            {analytics && analytics.engagementRate < 3 && (
              <div className={cn(fadeIn(200), "bg-amber-50 border border-amber-200 rounded-xl p-6")}>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 mb-2">Low Engagement Detected</h3>
                    <p className="text-amber-800 mb-4">
                      This project has an engagement rate of {analytics.engagementRate}%, which is below the recommended 3% threshold.
                    </p>
                    <Button 
                      size="sm" 
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={() => navigate(`/projects/${selectedProject.id}`)}
                    >
                      Recommend Video Re-processing
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {analytics && (
              <>
                {/* Key Metrics */}
                <div className={cn(fadeIn(300), "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6")}>
                  <MetricCard
                    icon={Eye}
                    title="Total Views"
                    value={analytics.totalViews.toLocaleString()}
                    subtitle="Across all platforms"
                    trend={Math.floor(Math.random() * 20 + 5)}
                    color="blue"
                  />
                  <MetricCard
                    icon={Users}
                    title="Impressions"
                    value={analytics.totalImpressions.toLocaleString()}
                    subtitle="Total reach"
                    trend={Math.floor(Math.random() * 15 + 8)}
                    color="green"
                  />
                  <MetricCard
                    icon={Heart}
                    title="Engagement Rate"
                    value={`${analytics.engagementRate}%`}
                    subtitle="Likes, comments, shares"
                    trend={analytics.engagementRate > 5 ? Math.floor(Math.random() * 10 + 2) : -Math.floor(Math.random() * 5 + 1)}
                    color="purple"
                  />
                  <MetricCard
                    icon={MousePointer}
                    title="Click-through Rate"
                    value={`${analytics.clickThroughRate}%`}
                    subtitle="Profile visits"
                    trend={Math.floor(Math.random() * 8 + 1)}
                    color="primary"
                  />
                </div>

                {/* Charts Row */}
                <div className={cn(fadeIn(400), "grid grid-cols-1 lg:grid-cols-2 gap-6")}>
                  <SimpleBarChart
                    data={analytics.weeklyData}
                    title="Weekly Views"
                  />
                  <SimpleBarChart
                    data={Object.entries(analytics.platformBreakdown).map(([label, value]) => ({ label, value }))}
                    title="Platform Breakdown"
                  />
                </div>

                {/* Engagement Metrics */}
                <div className={cn(fadeIn(500), "grid grid-cols-1 md:grid-cols-3 gap-6")}>
                  <div className="bg-background rounded-xl border border-border p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <Heart className="h-5 w-5 text-red-500" />
                      <h3 className="font-semibold">Likes</h3>
                    </div>
                    <p className="text-3xl font-bold text-red-600">{analytics.likes.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {((analytics.likes / analytics.totalViews) * 100).toFixed(2)}% of views
                    </p>
                  </div>

                  <div className="bg-background rounded-xl border border-border p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <MessageCircle className="h-5 w-5 text-blue-500" />
                      <h3 className="font-semibold">Comments</h3>
                    </div>
                    <p className="text-3xl font-bold text-blue-600">{analytics.comments.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {((analytics.comments / analytics.totalViews) * 100).toFixed(2)}% of views
                    </p>
                  </div>

                  <div className="bg-background rounded-xl border border-border p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <Share2 className="h-5 w-5 text-green-500" />
                      <h3 className="font-semibold">Shares</h3>
                    </div>
                    <p className="text-3xl font-bold text-green-600">{analytics.shares.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {((analytics.shares / analytics.totalViews) * 100).toFixed(2)}% of views
                    </p>
                  </div>
                </div>

                {/* Performance Summary */}
                <div className={cn(fadeIn(600), "bg-background rounded-xl border border-border p-6")}>
                  <h3 className="text-lg font-semibold mb-6">Performance Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-medium mb-4 flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        This Week's Performance
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Average daily views</span>
                          <span className="font-medium">{Math.floor(analytics.totalViews / 7).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Peak day</span>
                          <span className="font-medium">
                            {analytics.weeklyData.reduce((prev: any, current: any) => 
                              prev.views > current.views ? prev : current
                            ).day}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">New subscribers</span>
                          <span className="font-medium text-green-600">+{analytics.subscribers}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-4 flex items-center">
                        <TrendingUp className="h-4 w-4 mr-2" />
                        Key Insights
                      </h4>
                      <div className="space-y-3 text-sm">
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-blue-800">
                            Your best performing platform is <strong>
                              {Object.entries(analytics.platformBreakdown).reduce((a, b) => a[1] > b[1] ? a : b)[0]}
                            </strong>
                          </p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-green-800">
                            Engagement is <strong>
                              {analytics.engagementRate > 5 ? 'excellent' : 
                               analytics.engagementRate > 3 ? 'good' : 'below average'}
                            </strong> compared to industry standards
                          </p>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-purple-800">
                            Your audience is most active on <strong>
                              {analytics.weeklyData.reduce((prev: any, current: any) => 
                                prev.views > current.views ? prev : current
                              ).day}s
                            </strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard; 